// SmartOptionPricerPanel — lifted verbatim out of LiveTrading.tsx (God 2026-08-26).
// Same code, smaller address: a UI problem in this panel is now a 310-line file
// to read instead of a 6,900-line one. Imports are derived from what the block
// actually references, not guessed.

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Crosshair, RefreshCw, Search, X } from 'lucide-react';
import { OPTION_CHAIN_URL, OPTION_CHAIN_INTERVAL, _normSym, useActiveSymbols, OptionChainRow, fmtM, fetchTimeout, PRICER_SYMBOL_GROUPS } from './foundation';

export const SmartOptionPricerPanel: React.FC<{
  symbol: string;
  spot: number;
  expiry: string;
  open: boolean;
  onClose: () => void;
  onPickStrike: (strike: number, optType: 'CE'|'PE', ltp: number) => void;
  onSymbolChange: (s: string) => void;
}> = ({ symbol, spot, expiry, open, onClose, onPickStrike, onSymbolChange }) => {
  const actSyms = useActiveSymbols(open);   // PASS-hui symbols hi chips me (God 2026-07-14)
  const [chain, setChain] = useState<OptionChainRow[]>([]);
  const [pcr, setPcr] = useState(0);
  const [maxPain, setMaxPain] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState('');
  const [availableExpiries, setAvailableExpiries] = useState<string[]>([]);
  const [activeExpiry, setActiveExpiry] = useState('');
  // Spot from the chain response itself (underlyingValue/spot/atmStrike). The
  // `spot` prop is 0 for commodities (GOLD/CRUDE) — the parent only tracks index
  // spots — so SPOT showed ₹0.00 even though the chain carried 149836.
  const [chainSpot, setChainSpot] = useState(0);
  // Smart-search selector state
  const [symSearch, setSymSearch] = useState('');
  const [symCat, setSymCat] = useState<'ALL'|'INDEX'|'COMMODITY'|'STOCKS'>('ALL');

  // Use refs so the interval closure always sees latest symbol/expiry
  // without needing to restart the timer on every state change.
  const symbolRef      = useRef(symbol);
  const activeExpiryRef = useRef(activeExpiry);
  useEffect(() => { symbolRef.current = symbol; },       [symbol]);
  useEffect(() => { activeExpiryRef.current = activeExpiry; }, [activeExpiry]);

  const fetchChain = useCallback(async () => {
    const sym = symbolRef.current;
    const exp = activeExpiryRef.current;
    if (!sym || !open) return;
    setLoading(true);
    const { signal: abortSig, clear } = fetchTimeout(12000);
    try {
      const r = await fetch(
        `${OPTION_CHAIN_URL}?symbol=${sym}&expiry=${exp || 'current'}&t=${Date.now()}`,
        { cache: 'no-store', signal: abortSig },
      );
      if (r.ok) {
        const j = await r.json();
        if (Array.isArray(j.expiries_available) && j.expiries_available.length > 0) {
          setAvailableExpiries(j.expiries_available);
          if (!activeExpiryRef.current) setActiveExpiry(j.expiries_available[0]);
        }
        // Response structure: j.chains[expiry].chain (nested)
        let rows: OptionChainRow[] = [];
        if (j.chains && typeof j.chains === 'object') {
          const firstKey = Object.keys(j.chains)[0];
          if (firstKey) {
            const expData = j.chains[firstKey];
            rows = expData.chain ?? expData.data ?? expData.rows ?? [];
          }
        }
        if (!rows.length) {
          rows = j.chain ?? j.data ?? j.options ?? j.rows ?? (Array.isArray(j) ? j : []);
        }
        if (Array.isArray(rows) && rows.length > 0) {
          setChain(rows);
          setChainSpot(Number(j.spot ?? j.underlyingValue ?? j.atmStrike ?? 0) || 0);
          setPcr(j.pcr ?? j.put_call_ratio ?? 0);
          setMaxPain(j.max_pain ?? j.maxpain ?? 0);
          setLastUpdate(new Date().toLocaleTimeString('en-IN', { hour12: false }));
        }
      }
    } catch { /* silent — retry on next tick */ } finally {
      setLoading(false);
      clear();
    }
  }, [open]); // stable — reads symbol/expiry from refs, no interval restarts

  // Reset chain + expiry when user switches index
  useEffect(() => {
    setChain([]);
    setAvailableExpiries([]);
    setActiveExpiry('');
  }, [symbol]);

  // Single stable interval — never restarts except when panel opens/closes
  useEffect(() => {
    if (!open) return;
    fetchChain();                                            // immediate first load
    // God 2026-07-31 (persistent paper-test): PERSISTENT live data — poll even when
    // the tab/window is not focused so the strike-picker chain never pauses
    // mid-session (hidden-pause removed on the Master Admin's order).
    const id = setInterval(() => { fetchChain(); }, OPTION_CHAIN_INTERVAL);
    return () => clearInterval(id);
  }, [open, fetchChain]);

  if (!open) return null;

  // Real underlying — chain response wins (carries commodity spot); prop is the
  // fallback for indices whose live spot the parent already tracks.
  const liveSpot = chainSpot || spot;
  // Find ATM row
  const atmRow = chain.find(r => r.is_atm) || chain.reduce((best, r) =>
    !best || Math.abs(r.strike - liveSpot) < Math.abs(best.strike - liveSpot) ? r : best, null as OptionChainRow | null);

  return (
    <div className="lt-modal-overlay" style={{position:'fixed',inset:0,background:'rgba(0,0,0,.85)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:20}}>
      <div className="lt-modal-box" style={{background:'linear-gradient(135deg,#1e293b,#0f172a)',borderRadius:14,width:'95%',maxWidth:1080,maxHeight:'92vh',display:'flex',flexDirection:'column',border:'1px solid #334155',overflow:'hidden'}}>
        {/* Header */}
        <div style={{padding:'14px 20px',borderBottom:'1px solid #334155',display:'flex',justifyContent:'space-between',alignItems:'center',background:'linear-gradient(90deg,rgba(168,85,247,.08),rgba(34,211,238,.08))'}}>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <Crosshair size={20} color="#a855f7"/>
            <div>
              <div style={{fontSize:14,fontWeight:900,color:'#fff',letterSpacing:1}}>SMART OPTION PRICER</div>
              <div style={{fontSize:9,color:'#94a3b8',marginTop:2,display:'flex',gap:10,alignItems:'center'}}>
                <span>{symbol}</span>
                <span style={{color:'#fbbf24'}}>SPOT ₹{liveSpot?.toFixed(2)}</span>
                <span style={{color:'#a855f7'}}>EXP {activeExpiry || expiry || '…'}</span>
                {lastUpdate && <span style={{color:'#10b981'}}>● {lastUpdate}</span>}
              </div>
            </div>
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <button onClick={fetchChain} className="lt-btn" style={{padding:'6px 10px',background:'#1e293b',color:'#94a3b8',fontSize:9,border:'1px solid #334155'}}>
              <RefreshCw size={11} className={loading?'pulse-dot':''}/> REFRESH
            </button>
            <button onClick={onClose} style={{background:'none',border:'none',color:'#fff',cursor:'pointer'}}><X size={20}/></button>
          </div>
        </div>

        {/* Smart Symbol Selector — search + category + filtered grid */}
        <div style={{padding:'8px 16px',background:'#080d1a',borderBottom:'1px solid #1a2138'}}>
          {/* Row 1: search + category pills */}
          <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:7,flexWrap:'wrap'}}>
            <div style={{position:'relative',flex:'1 1 200px',minWidth:160}}>
              <Search size={12} color="#475569" style={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)'}}/>
              <input
                value={symSearch}
                onChange={e => setSymSearch(e.target.value)}
                placeholder="Search symbol… (NIFTY, RELIANCE, GOLD)"
                style={{width:'100%',padding:'6px 10px 6px 28px',borderRadius:7,fontSize:11,fontWeight:600,
                  background:'rgba(255,255,255,.04)',border:'1px solid #334155',color:'#e2e8f0',outline:'none'}}
              />
              {symSearch && (
                <button onClick={()=>setSymSearch('')} style={{position:'absolute',right:6,top:'50%',transform:'translateY(-50%)',
                  background:'none',border:'none',color:'#64748b',cursor:'pointer',padding:0,lineHeight:0}}><X size={12}/></button>
              )}
            </div>
            {(['ALL','INDEX','COMMODITY','STOCKS'] as const).map(c => (
              <button key={c} onClick={()=>setSymCat(c)}
                style={{padding:'5px 11px',borderRadius:6,fontSize:9,fontWeight:800,cursor:'pointer',whiteSpace:'nowrap',
                  background: symCat===c ? 'rgba(34,211,238,.2)' : 'rgba(255,255,255,.03)',
                  border: symCat===c ? '1px solid #22d3ee' : '1px solid #334155',
                  color: symCat===c ? '#67e8f9' : '#64748b'}}>{c}</button>
            ))}
          </div>
          {/* Row 2: filtered symbol buttons (max-height scroll) */}
          <div style={{display:'flex',gap:6,flexWrap:'wrap',maxHeight:96,overflowY:'auto'}}>
            {(() => {
              const q = symSearch.trim().toUpperCase();
              const groups = PRICER_SYMBOL_GROUPS.filter(g => symCat==='ALL' || g.cat===symCat);
              // sirf PASS-hui (active) symbols — fail-open jab tak list load na ho
              const matches = groups.flatMap(g => g.syms)
                .filter(s => (!q || s.includes(q)) && (!actSyms || actSyms.has(_normSym(s))));
              if (!matches.length) return (
                <span style={{fontSize:10,color:'#475569',padding:'6px 2px'}}>No symbol matches “{symSearch}”</span>
              );
              return matches.map(sym => (
                <button key={sym} onClick={() => { onSymbolChange(sym); }}
                  style={{padding:'5px 12px',borderRadius:6,fontSize:10,fontWeight:800,cursor:'pointer',whiteSpace:'nowrap',
                    transition:'all .15s',
                    background: symbol === sym ? 'rgba(168,85,247,.25)' : 'rgba(255,255,255,.04)',
                    border: symbol === sym ? '1px solid #a855f7' : '1px solid #334155',
                    color: symbol === sym ? '#c084fc' : '#94a3b8'}}>{sym}</button>
              ));
            })()}
          </div>
        </div>

        {/* Expiry Selector Row */}
        {availableExpiries.length > 0 && (
          <div style={{display:'flex',gap:5,padding:'6px 16px',background:'#060a14',borderBottom:'1px solid #1a2138',overflowX:'auto',flexWrap:'nowrap',alignItems:'center'}}>
            <span style={{fontSize:9,color:'#475569',whiteSpace:'nowrap',marginRight:4}}>EXPIRY:</span>
            {availableExpiries.slice(0, 8).map(exp => (
              <button key={exp} onClick={() => setActiveExpiry(exp)}
                style={{
                  padding:'4px 10px', borderRadius:5, fontSize:9, fontWeight:700,
                  cursor:'pointer', whiteSpace:'nowrap', transition:'all .15s',
                  background: activeExpiry === exp ? 'rgba(251,191,36,.2)' : 'rgba(255,255,255,.03)',
                  border: activeExpiry === exp ? '1px solid #fbbf24' : '1px solid #1e293b',
                  color: activeExpiry === exp ? '#fbbf24' : '#64748b',
                }}>{exp}</button>
            ))}
          </div>
        )}

        {/* Analytics Strip */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,padding:'12px 20px',background:'#0d1117',borderBottom:'1px solid #1a1f3a'}}>
          {[
            {l:'PCR',val:pcr.toFixed(2),col:pcr > 1 ? '#10b981' : '#f87171',sub:pcr > 1 ? 'Bullish' : 'Bearish'},
            {l:'Max Pain',val:`₹${maxPain.toFixed(0)}`,col:'#fbbf24',sub:`Δ ${(maxPain - liveSpot).toFixed(0)}`},
            {l:'ATM Strike',val:atmRow?`₹${atmRow.strike}`:'—',col:'#a855f7',sub:atmRow?`CE ₹${atmRow.ce_ltp?.toFixed(2)} · PE ₹${atmRow.pe_ltp?.toFixed(2)}`:''},
            {l:'Strikes Loaded',val:chain.length.toString(),col:'#38bdf8',sub:chain.length > 0 ? '🟢 LIVE' : '🔴 LOADING'},
          ].map((s,i) => (
            <div key={i} style={{padding:'8px 12px',background:'linear-gradient(135deg,#1e293b,#0f172a)',borderRadius:8,border:`1px solid ${s.col}33`}}>
              <div style={{fontSize:8,color:'#64748b',fontWeight:700,letterSpacing:1,textTransform:'uppercase'}}>{s.l}</div>
              <div style={{fontSize:16,fontWeight:900,color:s.col,fontFamily:'monospace',marginTop:2}}>{s.val}</div>
              <div style={{fontSize:8,color:'#94a3b8',marginTop:2}}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Smart Pick Buttons */}
        <div style={{padding:'10px 20px',background:'rgba(168,85,247,.04)',borderBottom:'1px solid #1a1f3a'}}>
          <div style={{fontSize:9,color:'#a855f7',fontWeight:800,marginBottom:8,letterSpacing:1}}>🔥 ONE-CLICK SMART PICK (auto-strike + real LTP)</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:6}}>
            {[
              {mode:'OTM2', off:2, type:'CE' as const, label:'OTM2 CE'},
              {mode:'OTM1', off:1, type:'CE' as const, label:'OTM1 CE'},
              {mode:'ATM',  off:0, type:'CE' as const, label:'ATM CE'},
              {mode:'ATM',  off:0, type:'PE' as const, label:'ATM PE'},
              {mode:'OTM1', off:-1,type:'PE' as const, label:'OTM1 PE'},
              {mode:'OTM2', off:-2,type:'PE' as const, label:'OTM2 PE'},
            ].map((p,i) => {
              if (!atmRow) return <div key={i} style={{padding:8,fontSize:9,color:'#475569',textAlign:'center'}}>—</div>;
              const atmIdx = chain.findIndex(c => c.strike === atmRow.strike);
              const targetIdx = atmIdx + p.off;
              const targetRow = chain[targetIdx];
              if (!targetRow) return <div key={i} style={{padding:8,fontSize:9,color:'#475569',textAlign:'center'}}>—</div>;
              const ltp = p.type === 'CE' ? targetRow.ce_ltp : targetRow.pe_ltp;
              const col = p.type === 'CE' ? '#10b981' : '#ef4444';
              return (
                <button key={i} onClick={() => onPickStrike(targetRow.strike, p.type, ltp)}
                  style={{padding:'8px 6px',background:`linear-gradient(135deg,${col}22,${col}11)`,border:`1px solid ${col}55`,borderRadius:6,cursor:'pointer',color:'#fff',transition:'all .15s'}}
                  onMouseEnter={e=>e.currentTarget.style.transform='translateY(-2px)'}
                  onMouseLeave={e=>e.currentTarget.style.transform='translateY(0)'}>
                  <div style={{fontSize:9,fontWeight:800,color:col,letterSpacing:1}}>{p.label}</div>
                  <div style={{fontSize:11,fontWeight:900,color:'#fff',fontFamily:'monospace',marginTop:2}}>{targetRow.strike}</div>
                  <div style={{fontSize:11,fontWeight:800,color:'#fbbf24',fontFamily:'monospace',marginTop:2}}>₹{ltp?.toFixed(2)}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Option Chain Table */}
        <div className="lt-scroll" style={{flex:1,overflowY:'auto',padding:'8px 20px 20px'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
            <thead>
              <tr style={{background:'#0d1117',color:'#94a3b8',position:'sticky',top:0,zIndex:1}}>
                <th colSpan={3} style={{padding:'8px',textAlign:'center',color:'#10b981',borderBottom:'1px solid #1e293b',fontSize:11,letterSpacing:2}}>──── CALL (CE) ────</th>
                <th style={{padding:'8px',background:'#1a1f3a',color:'#fbbf24',fontWeight:900,letterSpacing:1}}>STRIKE</th>
                <th colSpan={3} style={{padding:'8px',textAlign:'center',color:'#ef4444',borderBottom:'1px solid #1e293b',fontSize:11,letterSpacing:2}}>──── PUT (PE) ────</th>
              </tr>
              <tr style={{background:'#0d1117',color:'#64748b',fontSize:9,position:'sticky',top:32,zIndex:1}}>
                <th style={{padding:'6px 8px',textAlign:'right',fontWeight:700}}>OI</th>
                <th style={{padding:'6px 8px',textAlign:'right',fontWeight:700}}>VOL</th>
                <th style={{padding:'6px 8px',textAlign:'right',fontWeight:700,cursor:'pointer'}}>LTP ↗</th>
                <th style={{padding:'6px 8px',textAlign:'center',fontWeight:900,color:'#fbbf24'}}>STRIKE</th>
                <th style={{padding:'6px 8px',textAlign:'right',fontWeight:700,cursor:'pointer'}}>↗ LTP</th>
                <th style={{padding:'6px 8px',textAlign:'right',fontWeight:700}}>VOL</th>
                <th style={{padding:'6px 8px',textAlign:'right',fontWeight:700}}>OI</th>
              </tr>
            </thead>
            <tbody>
              {chain.length === 0 && (
                <tr><td colSpan={7} style={{padding:40,textAlign:'center',color:'#475569'}}>
                  {loading ? 'Loading option chain…' : 'No data — backend /option-chain endpoint required'}
                </td></tr>
              )}
              {chain.map((r,i) => {
                const isAtm = r.is_atm;
                const ceItm = r.strike < spot;
                const peItm = r.strike > spot;
                return (
                  <tr key={i} style={{
                    background: isAtm ? 'rgba(251,191,36,.1)' : (i%2===0?'#0a0f1a':'transparent'),
                    borderBottom:'1px solid #1a1f3a'
                  }}>
                    <td style={{padding:'6px 8px',textAlign:'right',color:'#94a3b8',fontFamily:'monospace',background:ceItm?'rgba(16,185,129,.05)':'transparent'}}>{fmtM(r.ce_oi||0)}</td>
                    <td style={{padding:'6px 8px',textAlign:'right',color:'#64748b',fontFamily:'monospace',background:ceItm?'rgba(16,185,129,.05)':'transparent'}}>{fmtM(r.ce_volume||0)}</td>
                    <td onClick={()=>r.ce_ltp && onPickStrike(r.strike,'CE',r.ce_ltp)} style={{padding:'6px 8px',textAlign:'right',color:'#10b981',fontFamily:'monospace',fontWeight:800,cursor:'pointer',background:ceItm?'rgba(16,185,129,.05)':'transparent'}} className="strike-cell">
                      ₹{r.ce_ltp?.toFixed(2)}
                    </td>
                    <td style={{padding:'6px 8px',textAlign:'center',fontWeight:900,color:isAtm?'#fbbf24':'#fff',fontFamily:'monospace',fontSize:11,background:isAtm?'rgba(251,191,36,.15)':'#1a1f3a'}}>
                      {r.strike}{isAtm && <span style={{fontSize:7,marginLeft:4,padding:'1px 4px',background:'#fbbf24',color:'#000',borderRadius:3}}>ATM</span>}
                    </td>
                    <td onClick={()=>r.pe_ltp && onPickStrike(r.strike,'PE',r.pe_ltp)} style={{padding:'6px 8px',textAlign:'right',color:'#ef4444',fontFamily:'monospace',fontWeight:800,cursor:'pointer',background:peItm?'rgba(239,68,68,.05)':'transparent'}} className="strike-cell">
                      ₹{r.pe_ltp?.toFixed(2)}
                    </td>
                    <td style={{padding:'6px 8px',textAlign:'right',color:'#64748b',fontFamily:'monospace',background:peItm?'rgba(239,68,68,.05)':'transparent'}}>{fmtM(r.pe_volume||0)}</td>
                    <td style={{padding:'6px 8px',textAlign:'right',color:'#94a3b8',fontFamily:'monospace',background:peItm?'rgba(239,68,68,.05)':'transparent'}}>{fmtM(r.pe_oi||0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{padding:'10px 20px',background:'#070b13',borderTop:'1px solid #1e293b',fontSize:9,color:'#64748b',display:'flex',justifyContent:'space-between'}}>
          <span>💡 Click any LTP to instantly pick that strike</span>
          <span>Auto-refresh every 4s · Real Dhan data</span>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
//  🆕 v6.0 — LIVE SIGNAL STREAM PANEL (last 20 webhook signals)
//  🆕 v7.0 — Now supports minimize/maximize state
// ══════════════════════════════════════════════════════════════
// RUDRA live-signal feed (VAG/SHOOL/STHAMBH) per tab — /ceo-feed
